import Big from 'big.js';
import { DateTime } from 'luxon';
import * as buttons from 'smg/buttons';
import { GetQuotes, OpenQuoteInvoice } from 'smg/customer-api/v2/quotes';
import { ShowError } from 'smg/errors';
import { GetLineItems } from 'smg/customer-api/v2/lineitems';
import * as models from 'smg/customer-api/models';
import * as html from 'smg/html';
import { GetProofURLs } from 'smg/proofs';
import * as template from 'smg/template';
import { DollarFormat } from 'smg/util';
import { PageLoader } from '../account';
import { AccountReady } from '../account-session';
import * as carts from '../carts';
import { Relref } from '../hugo';
import { SetupRevisionLink } from '../quotes';

declare interface myWindow extends Window {
    readonly cloudFront: string;
}
declare const window: myWindow;

void main();

async function main() {
    if (await AccountReady) {
        return;
    }

    const lineItems = GetLineItems({ data: 'name,pluralName,product' });

    const pl: PageLoader<models.Quote> = new PageLoader<models.Quote>({
        get: () => GetQuotes({
            sort: pl.sort(),
            max: pl.currentPageSize(),
            data: 'serial,created,quoteLineItems,quoteProofs,cachedTotal,quoteOrders,quoteRequest,key',
            p: pl.currentPage(),
            q: pl.search(),
        }).then(data => ({ rows: data.results, nextPage: data.nextPage })),
        draw: async quote => {
            const el = template.Clone('#quote-template').firstElementChild as HTMLElement;

            const serial = el.querySelector('.quote-serial') as HTMLElement;
            serial.textContent = String(quote.serial);

            const date = el.querySelector('.quote-date') as HTMLElement;
            date.textContent = quote.created?.isValid ? quote.created.toLocaleString(DateTime.DATE_SHORT) : '';

            const total = Big(quote.cachedTotal || 0);
            const shipping = Big(0);

            const subtotal = total.sub(shipping);

            const subtotalEls = el.querySelectorAll('.quote-subtotal');
            subtotalEls.forEach(el => el.textContent = DollarFormat(subtotal));

            const shippingEls = el.querySelectorAll('.quote-shipping');
            shippingEls.forEach(el => el.textContent = DollarFormat(shipping));

            const totalEls = el.querySelectorAll('.quote-total');
            totalEls.forEach(el => el.textContent = DollarFormat(total));

            const addToCart = el.querySelector('.quote-add-to-cart') as HTMLButtonElement;
            addToCart.addEventListener('click', () => {
                buttons.Start(addToCart);
                void carts.AddCartItem({ quoteID: quote.key }).then(() => {
                    addToCart.classList.remove('btn-primary', 'btn-success', 'text-white');
                    addToCart.classList.add('btn-outline-success');

                    const $icon = addToCart.querySelector('.material-symbols-sharp');
                    if ($icon) {
                        $icon.textContent = 'check';
                    }
                }).finally(() => buttons.Stop(addToCart));
            });

            const invoice = el.querySelector('.quote-invoice') as HTMLButtonElement;
            invoice.addEventListener('click', () => {
                buttons.Start(invoice);
                void OpenQuoteInvoice(`id:${quote.id ?? ''}`).catch(ShowError).finally(() => buttons.Stop(invoice));
            });

            const proofsContainer = el.querySelector('.quote-proofs-container') as HTMLElement;
            const proofsParent = el.querySelector('.quote-proofs-parent') as HTMLElement;
            if (quote.quoteProofs.length) {
                for (const op of quote.quoteProofs) {
                    const p = op.proof;
                    if (!p) {
                        continue;
                    }

                    const urls = GetProofURLs(window.cloudFront, p.file || '');

                    const a = template.Clone('#quote-proof-template').firstElementChild as HTMLAnchorElement;
                    a.href = urls.main;
                    a.setAttribute('data-fancybox', quote.id || '');

                    const img = a.querySelector('img') as HTMLImageElement;
                    img.src = urls.thumb;
                    img.alt = op.name || '';

                    const $proofName = a.querySelector('.proof-name') as HTMLElement | null;
                    if (op.name) {
                        if ($proofName) {
                            $proofName.textContent = op.name;
                            $proofName.title = op.name;
                        }
                    } else {
                        $proofName?.remove();
                    }

                    proofsParent.appendChild(a);
                }
            } else {
                proofsContainer.parentElement?.removeChild(proofsContainer);
            }

            if (quote.quoteLineItems.length) {
                const qtys: Record<string, number> = {};
                for (const quoteLineItem of quote.quoteLineItems) {
                    if (!quoteLineItem.quantity) {
                        continue;
                    }

                    const lineItem = (await lineItems).get(quoteLineItem.lineItemID || '');

                    if (!(quoteLineItem.lineItem || lineItem?.product)) {
                        continue;
                    }

                    const name = quoteLineItem.lineItem || lineItem?.pluralName || lineItem?.name || 'Other';
                    qtys[name] = (qtys[name] || 0) + quoteLineItem.quantity;
                }

                const summary = el.querySelector('.quote-summary') as HTMLElement;
                summary.textContent = Object.keys(qtys).map(n => `${qtys[n]} ${n}`).join(', ');
            }

            const orderHistoryContainer = el.querySelector('.quote-order-history-container') as HTMLElement;
            if (quote.quoteOrders.length) {
                const orderHistory = el.querySelector('.quote-order-history') as HTMLElement;
                for (const quoteOrder of quote.quoteOrders) {
                    const order = quoteOrder.order;
                    const orderDate = order?.created.isValid ? order.created.toLocaleString(DateTime.DATE_SHORT) : '';
                    const li = html.FromString(`<li>
                        <a href="${Relref('/orders')}?q=${order?.invoiceNumber || ''}">#${order?.invoiceNumber || ''}</a> - ${orderDate}
                    </li>`).firstElementChild as HTMLLIElement;
                    orderHistory.appendChild(li);
                }
            } else {
                orderHistoryContainer.parentElement?.removeChild(orderHistoryContainer);
            }

            const projectContainer = el.querySelector('.quote-project-container') as HTMLElement;
            if (quote.quoteRequest?.quoteRequestProject?.name) {
                const project = el.querySelector('.quote-project') as HTMLElement;
                project.textContent = quote.quoteRequest.quoteRequestProject.name;
            } else {
                projectContainer.parentElement?.removeChild(projectContainer);
            }

            const quoteLink = el.querySelector('.quote-link') as HTMLAnchorElement | null;
            if (quoteLink) {
                quoteLink.href = `${Relref('/quote')}?id=${quote.id || ''}`;
            }

            const revisionLink = el.querySelector('.quote-request-revision') as HTMLAnchorElement;
            const revBase = `${Relref('/revisions')}?id=${quote.key}`;
            SetupRevisionLink(revisionLink, quote.quoteProofs, window.cloudFront, revBase);

            return el;
        },
    });

    pl.load();
}
